import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import filesize from 'filesize';
import { saveAs } from 'file-saver';
import Button from './Button';
import FileIcon from './FileIcon';
import { sendEvent } from '../utils/analytics';

const extensions = {
  excel: 'Excel',
  jpg: 'JPG',
  pdf: 'PDF',
  png: 'PNG',
  ppt: 'PPT',
  word: 'Word',
  html: 'HTML',
};

const convertionTips = {
  excel: 'file-table.scanned-pages-as-images',
  ppt: 'file-table.scanned-pages-as-images',
  word: 'file-table.scanned-pages-as-images',
};

const FileTable = ({
  className,
  fileType,
  fileName,
  fileSize,
  fileSizeCompressed,
  fileUrl,
  compressable,
  convertTo,
  convertStatus = 'idle',
  convertProgress,
  onConvert,
  onDownload,
}) => {
  const { t } = useTranslation('convert-and-edit');
  const size = useMemo(() => filesize(fileSize), [fileSize]);
  const sizeCompressed = useMemo(() => fileSizeCompressed && filesize(fileSizeCompressed), [fileSizeCompressed]);

  const itemFile = (
    <FileTableItem className="file-table__item--file">
      <FileTableAttr
        name={
          convertStatus === 'done'
            ? t('file-table.converted-file')
            : t('file-table.file')
        }
      >
        <FileIcon type={fileType} aria-hidden="true" />
        <span title={fileName}>
          {fileName}
        </span>
      </FileTableAttr>
    </FileTableItem>
  );

  let itemConvertionMode;

  if (convertTo && convertStatus !== 'done') {
    const extension = extensions[convertTo] || convertTo;
    const tip = convertionTips[convertTo];

    itemConvertionMode = (
      <FileTableItem className="file-table__item--convertion-mode">
        <FileTableAttr name={t('file-table.convertion-mode')} tip={tip && t(tip)}>
          <FileIcon type={convertTo} aria-hidden="true" />
          {t('file-table.convert-to', { extension })}
        </FileTableAttr>
      </FileTableItem>
    );
  }

  const itemSize = (
    <FileTableItem
      className={classNames(
        'file-table__item--size',
        !!fileSizeCompressed && 'file-table__item--size-compressed',
      )}
    >
      {fileSizeCompressed ? (
        <FileTableAttr
          name={t('file-table.new-size')}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t('file-table.size-comparison', {
                newSize: sizeCompressed,
                oldSize: size,
              }),
            }}
          />
        </FileTableAttr>
      ) : (
        <FileTableAttr name={t('file-table.size')}>
          {size}
        </FileTableAttr>
      )}
    </FileTableItem>
  );

  let itemCompress;

  const handleCompressButtonClick = useCallback(() => {
    sendEvent({
      category: 'file-table',
      action: 'compress-click',
    });
  }, []);

  if (fileType === 'pdf' && !convertTo && !fileSizeCompressed && compressable) {
    itemCompress = (
      <FileTableItem className="file-table__item--compress">
        <div className="file-table__secondary-action">
          <Button
            className="file-table__secondary-action__button"
            as="a"
            size="lg"
            variant="secondary"
            href="/compress"
            onClick={handleCompressButtonClick}
          >
            {t('file-table.compress')}
          </Button>
        </div>
      </FileTableItem>
    );
  }

  let itemCompression;

  if (fileSizeCompressed) {
    let text;

    if (fileSizeCompressed <= fileSize) {
      text = t('file-table.compression-smaller-by', {
        percentage: ((fileSize - fileSizeCompressed) / fileSize * 100).toFixed(1),
      });
    } else {
      text = t('file-table.compression-bigger-by', {
        percentage: ((fileSizeCompressed - fileSize) / fileSize * 100).toFixed(1),
      });
    }

    itemCompression = (
      <FileTableItem className="file-table__item--compression">
        <FileTableAttr name={t('file-table.compression')}>
          <span dangerouslySetInnerHTML={{ __html: text }} />
        </FileTableAttr>
      </FileTableItem>
    );
  }

  const primaryActionButtonProps = {};

  const handleConvertClick = useCallback(() => {
    onConvert?.();

    sendEvent({
      category: 'file-table',
      action: 'convert-click',
      label: fileType,
    });
  }, [fileType, onConvert]);

  const handleDownloadClick = useCallback((event) => {
    event.preventDefault();
    saveAs(event.currentTarget.href, event.currentTarget.download);
    onDownload?.();

    sendEvent({
      category: 'file-table',
      action: 'download-click',
      label: fileType,
    });
  }, [fileType, onDownload]);

  if (convertTo && convertStatus === 'idle') {
    primaryActionButtonProps.variant = 'secondary';
    primaryActionButtonProps.onClick = handleConvertClick;
    primaryActionButtonProps.children = t('file-table.convert-file');
  } else if (convertTo && convertStatus === 'progress') {
    primaryActionButtonProps.variant = 'secondary';
    primaryActionButtonProps.progress = convertProgress;
    primaryActionButtonProps.children = t('file-table.converting');
  } else {
    primaryActionButtonProps.as = 'a';
    primaryActionButtonProps.variant = 'primary';
    primaryActionButtonProps.href = fileUrl;
    primaryActionButtonProps.target = '_blank';
    primaryActionButtonProps.download = fileName || true;
    primaryActionButtonProps.onClick = handleDownloadClick;
    primaryActionButtonProps.children = t('file-table.download');
  }

  return (
    <div className={classNames('file-table', className)}>
      <div className="file-table__wrapper">
        <div className="file-table__items">
          {itemFile}
          {itemConvertionMode}
          {itemSize}
          {itemCompress}
          {itemCompression}
        </div>
        <div className="file-table__primary-action">
          <Button
            className="file-table__primary-action__button"
            size="lg"
            variant="primary"
            {...primaryActionButtonProps}
          />
        </div>
      </div>
    </div>
  );
};

const FileTableItem = ({ className, children }) => (
  <div className={classNames('file-table__item', className)}>
    {children}
  </div>
);

const FileTableAttr = ({ name, children, tip }) => (
  <div className="file-table__attr">
    <div className="file-table__attr__name">
      {name}
    </div>
    <div className="file-table__attr__value">
      {children}
    </div>
    {!!tip && (
      <div className="file-table__attr__tip">
        {tip}
      </div>
    )}
  </div>
);

FileTable.propTypes = {
  className: PropTypes.string,
  fileType: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  fileSize: PropTypes.number.isRequired,
  fileSizeCompressed: PropTypes.number,
  fileUrl: PropTypes.string,
  convertTo: PropTypes.string,
  convertStatus: PropTypes.oneOf([
    'idle',
    'progress',
    'done',
  ]),
  convertProgress: PropTypes.number,
  compressable: PropTypes.bool,
  onConvert: PropTypes.func,
  onDownload: PropTypes.func,
};

FileTableItem.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

FileTableAttr.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  tip: PropTypes.string,
};

export default FileTable;
